import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDateTime } from "../../api/dates";
import { formatMoney, formatQuantity } from "../../api/money";
import {
  MOVE_LABEL,
  STATUS_LABEL,
  movesFor,
  useDeleteQuotation,
  useMove,
  useRecordMileage,
  type Move,
  type QuotationDetail,
} from "../../api/quotations";
import { jobRef } from "../commercial/pipeline";

const NEEDS_NOTE: Move[] = ["reject", "return"];

/** Where the quotation stands in a sentence, from its own record. */
export function decisionText(detail: QuotationDetail): string {
  const decided = detail.decision;
  const latest = detail.history[0];
  const note = (text: string | null | undefined) => (text ? ` — “${text}”` : "");
  switch (detail.status) {
    case "approved":
    case "rejected": {
      const verb = detail.status === "approved" ? "Approved" : "Rejected";
      const by = decided?.by ? ` by ${decided.by}` : "";
      const at = decided?.at ? ` on ${formatDate(decided.at)}` : "";
      return `${verb}${by}${at}${note(decided?.note)}.`;
    }
    case "returned":
      return `Returned${latest?.at ? ` on ${formatDate(latest.at)}` : ""}${note(latest?.note)}. It comes back as a resubmission.`;
    case "draft":
      return "Being captured. Submit it for approval once every section 9 part is covered.";
    case "superseded":
      return "Superseded by a later quotation.";
    default:
      return `${STATUS_LABEL[detail.status]}${latest?.at ? ` since ${formatDate(latest.at)}` : ""}, waiting for a decision.`;
  }
}

/** The decision: what has been decided, and the moves the server allows from
 * here. A move that needs a note asks for one before it is sent. */
export function Decision({ detail }: { detail: QuotationDetail }) {
  const move = useMove(detail.id);
  const [pending, setPending] = useState<Move | null>(null);
  const [note, setNote] = useState("");
  const [boqReason, setBoqReason] = useState("");
  const [billSaid, setBillSaid] = useState<string | null>(null);
  const moves = movesFor(detail);
  const bill = detail.pre_issued_boq;
  const asksBoqReason = bill !== null && bill.higher && !bill.applied && moves.includes("approve");

  function perform(chosen: Move) {
    if (NEEDS_NOTE.includes(chosen) && pending !== chosen) {
      setPending(chosen);
      return;
    }
    move.mutate(
      {
        move: chosen,
        note: note.trim() || undefined,
        boq_reason: chosen === "approve" ? boqReason.trim() || undefined : undefined,
      },
      {
        onSuccess: (answer) => {
          setPending(null);
          setNote("");
          setBoqReason("");
          setBillSaid(answer.boq_reprice?.message ?? null);
        },
      },
    );
  }

  return (
    <section className="cm-block" aria-label="Decision">
      <h3>Decision</h3>
      <p className="cm-doc-note">{decisionText(detail)}</p>
      {moves.length === 0 ? (
        detail.status === "approved" || detail.status === "rejected" || detail.status === "superseded" ? (
          <p className="cm-doc-note">Nothing more can happen to this quotation from here.</p>
        ) : null
      ) : (
        <div className="move-buttons">
          {moves.map((chosen, index) => (
            <button
              key={chosen}
              type="button"
              className={index === 0 && !NEEDS_NOTE.includes(chosen) ? "btn btn-primary" : "btn btn-secondary"}
              disabled={move.isPending}
              aria-pressed={pending === chosen ? true : undefined}
              onClick={() => perform(chosen)}
            >
              {MOVE_LABEL[chosen]}
            </button>
          ))}
        </div>
      )}
      {pending !== null || moves.some((m) => !NEEDS_NOTE.includes(m)) ? (
        <label className="move-note">
          {pending !== null
            ? `Why is it being ${pending === "return" ? "returned" : "rejected"}? (the supplier reads this)`
            : "Note (optional)"}
          <textarea value={note} rows={2} onChange={(event) => setNote(event.target.value)} placeholder={pending !== null ? "Required" : ""} />
        </label>
      ) : null}
      {asksBoqReason ? (
        <label className="move-note">
          Why the BOQ adopts the higher price ({formatMoney(bill?.k1_quoted)} against the kit's {formatMoney(bill?.k1_now)};
          optional: left blank, the reprice waits on the BOQ)
          <textarea value={boqReason} rows={2} onChange={(event) => setBoqReason(event.target.value)} />
        </label>
      ) : null}
      {pending !== null ? (
        <div className="move-buttons">
          <button type="button" className="btn btn-primary" disabled={move.isPending || note.trim() === ""} onClick={() => perform(pending)}>
            Confirm: {MOVE_LABEL[pending]}
          </button>
          <button type="button" className="btn" onClick={() => setPending(null)}>
            Cancel
          </button>
        </div>
      ) : null}
      {billSaid ? <p className="notice">{billSaid}</p> : null}
      {move.isError ? <p className="notice bad">{move.error.message}</p> : null}
    </section>
  );
}

/** Deleting a quotation uploaded by mistake -- read twice, against the wrong
 * site, from the wrong supplier. Office only. It asks why before it acts, and
 * the answer goes on the site's history. Once the supplier holds the order or
 * the City's bill has moved on, it says what has to happen first instead. */
export function DeleteQuotation({ detail }: { detail: QuotationDetail }) {
  const remove = useDeleteQuotation(detail.id);
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  if (!detail.actions.office) {
    return null;
  }
  const ref = jobRef(detail.supplier, detail.number);
  if (!detail.can_delete) {
    return detail.delete_blocker ? (
      <section className="cm-block cm-delete" aria-label="Delete this quotation">
        <p className="cm-doc-note">Can't be deleted: {detail.delete_blocker}</p>
      </section>
    ) : null;
  }
  return (
    <section className="cm-block cm-delete" aria-label="Delete this quotation">
      {asking ? (
        <form
          aria-label={`Delete ${ref}`}
          onSubmit={(event) => {
            event.preventDefault();
            remove.mutate(reason.trim(), { onSuccess: () => navigate("/commercial") });
          }}
        >
          <p className="cm-doc-note">
            Deletes {ref} with its lines, and any order that never went to the supplier or bill still in draft. The site
            and its inspection stay.
          </p>
          <label className="move-note">
            Why is it being deleted? (goes on the site's history)
            <textarea value={reason} rows={2} onChange={(event) => setReason(event.target.value)} placeholder="Required" />
          </label>
          <div className="move-buttons">
            <button type="submit" className="btn btn-danger" disabled={remove.isPending || reason.trim() === ""}>
              {remove.isPending ? "Deleting…" : `Delete ${ref}`}
            </button>
            <button type="button" className="btn" onClick={() => setAsking(false)}>
              Keep it
            </button>
          </div>
          {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
        </form>
      ) : (
        <button type="button" className="row-button danger" onClick={() => setAsking(true)}>
          Delete this quotation…
        </button>
      )}
    </section>
  );
}

/** Kilometres driven against the route planned, for the office only. */
export function Mileage({ detail }: { detail: QuotationDetail }) {
  const mileage = useRecordMileage(detail.id);
  const [actualKm, setActualKm] = useState(detail.mileage.actual_km ?? "");
  const [kmReason, setKmReason] = useState("");
  if (!detail.actions.office) {
    return null;
  }
  const m = detail.mileage;
  const difference = m.difference_km ? ` (${m.difference_km.startsWith("-") ? "" : "+"}${formatQuantity(m.difference_km)} km)` : "";
  return (
    <section className="cm-block" aria-label="Mileage">
      <h3>Mileage</h3>
      <p className="cm-doc-note">
        Planned {m.planned_km ? `${formatQuantity(m.planned_km)} km` : "— no route yet"}; driven{" "}
        {m.actual_km ? `${formatQuantity(m.actual_km)} km${difference}` : "not recorded"}.
        {m.reason ? ` Reason: ${m.reason}.` : ""}
      </p>
      <form
        className="inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          mileage.mutate({ actual_km: actualKm, reason: kmReason });
        }}
      >
        <label>
          Kilometres driven
          <input value={actualKm} onChange={(event) => setActualKm(event.target.value)} placeholder="72,4" />
        </label>
        <label>
          Reason, if over tolerance
          <input value={kmReason} onChange={(event) => setKmReason(event.target.value)} />
        </label>
        <button type="submit" className="btn btn-secondary" disabled={mileage.isPending || actualKm.trim() === ""}>
          Record
        </button>
      </form>
      {mileage.isError ? <p className="notice bad">{mileage.error.message}</p> : null}
    </section>
  );
}

/** Every move the quotation made, and every price changed from the list. */
export function History({ detail }: { detail: QuotationDetail }) {
  return (
    <section className="cm-block" aria-label="History">
      <h3>History</h3>
      {detail.history.length === 0 ? (
        <p className="cm-doc-note">Not yet submitted.</p>
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Version</th>
                <th>Became</th>
                <th>By</th>
                <th>Note</th>
                <th className="num">Excl VAT</th>
              </tr>
            </thead>
            <tbody>
              {detail.history.map((row, index) => (
                <tr key={`${row.version}-${row.at}-${index}`}>
                  <td>{formatDateTime(row.at)}</td>
                  <td>{row.version}</td>
                  <td>{STATUS_LABEL[row.status]}</td>
                  <td>{row.by ?? "—"}</td>
                  <td>{row.note ?? ""}</td>
                  <td className="num">{formatMoney(row.total_excl_vat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail.overrides.length > 0 ? (
        <>
          <h4>Prices changed from the price list</h4>
          <ul className="cm-overrides">
            {detail.overrides.map((o, index) => (
              <li key={`${o.line_id}-${o.at}-${index}`}>
                Line {o.line_id}: {formatMoney(o.calculated)} → {formatMoney(o.revised)} — {o.reason ?? "no reason"} ({o.by ?? "—"},{" "}
                {formatDate(o.at)})
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
